import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, RotateCcw, Plus, Trash2, ChevronUp, ChevronDown,
  Pencil, X, Check, GripVertical, AlertCircle, MessageSquare, Shield
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { QUESTION_SECTIONS, SECTION_ORDER } from '../data/questions';

export default function QuestionManagementPage() {
  const navigate = useNavigate();
  const { communityId } = useParams();
  const { user } = useAuth();
  const functions = getFunctions();

  // Deep clone the defaults for initial state
  const cloneDefaults = () => ({
    sections: JSON.parse(JSON.stringify(QUESTION_SECTIONS)),
    sectionOrder: [...SECTION_ORDER]
  });

  const [sections, setSections] = useState({});
  const [sectionOrder, setSectionOrder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isCustomized, setIsCustomized] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Editing state
  const [editingQuestion, setEditingQuestion] = useState(null); // { sectionId, questionIndex }
  const [editingSection, setEditingSection] = useState(null); // sectionId being edited
  const [expandedSections, setExpandedSections] = useState({});

  // Modal state
  const [showAddQuestion, setShowAddQuestion] = useState(null); // sectionId
  const [showAddSection, setShowAddSection] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // { type: 'section'|'question', sectionId, questionIndex? }

  // New item state
  const [newQuestionText, setNewQuestionText] = useState('');
  const [newQuestionFollowUp, setNewQuestionFollowUp] = useState('');
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newSectionIcon, setNewSectionIcon] = useState('📝');
  const [newSectionDescription, setNewSectionDescription] = useState('');

  // Load questions on mount
  useEffect(() => {
    loadQuestions();
  }, [communityId]);

  const loadQuestions = async () => {
    setLoading(true);
    setError(null);

    try {
      const loadFn = httpsCallable(functions, 'loadCommunityQuestions');
      const result = await loadFn({ communityId });

      if (result.data.success && result.data.isCustomized) {
        setSections(result.data.questionBank.sections);
        setSectionOrder(result.data.questionBank.sectionOrder);
        setIsCustomized(true);
      } else {
        const defaults = cloneDefaults();
        setSections(defaults.sections);
        setSectionOrder(defaults.sectionOrder);
        setIsCustomized(false);
      }

      // Expand consent by default
      setExpandedSections({ consent: true });
    } catch (err) {
      console.error('Error loading questions:', err);
      setError('Failed to load questions');
      const defaults = cloneDefaults();
      setSections(defaults.sections);
      setSectionOrder(defaults.sectionOrder);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const saveFn = httpsCallable(functions, 'saveCommunityQuestions');
      const result = await saveFn({ communityId, sections, sectionOrder });

      if (result.data.success) {
        setIsCustomized(true);
        setHasChanges(false);
        setSuccessMessage(`Saved! ${result.data.totalQuestions} questions across ${sectionOrder.length} sections.`);
        setTimeout(() => setSuccessMessage(null), 4000);
      }
    } catch (err) {
      console.error('Error saving questions:', err);
      setError(err.message || 'Failed to save questions');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    setError(null);

    try {
      const resetFn = httpsCallable(functions, 'resetCommunityQuestions');
      await resetFn({ communityId });

      const defaults = cloneDefaults();
      setSections(defaults.sections);
      setSectionOrder(defaults.sectionOrder);
      setIsCustomized(false);
      setHasChanges(false);
      setShowResetConfirm(false);
      setSuccessMessage('Reset to default questions.');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      console.error('Error resetting questions:', err);
      setError(err.message || 'Failed to reset questions');
    } finally {
      setSaving(false);
    }
  };

  // Mark changes
  const markChanged = () => setHasChanges(true);

  // Toggle section expand
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  // =============================================
  // Question editing
  // =============================================

  const updateQuestion = (sectionId, questionIndex, field, value) => {
    setSections(prev => {
      const updated = { ...prev };
      updated[sectionId] = {
        ...updated[sectionId],
        questions: [...updated[sectionId].questions]
      };
      updated[sectionId].questions[questionIndex] = {
        ...updated[sectionId].questions[questionIndex],
        [field]: value
      };
      return updated;
    });
    markChanged();
  };

  const deleteQuestion = (sectionId, questionIndex) => {
    setSections(prev => {
      const updated = { ...prev };
      updated[sectionId] = {
        ...updated[sectionId],
        questions: updated[sectionId].questions.filter((_, i) => i !== questionIndex)
      };
      return updated;
    });
    setShowDeleteConfirm(null);
    markChanged();
  };

  const addQuestion = (sectionId) => {
    if (!newQuestionText.trim()) return;

    const sectionQuestions = sections[sectionId].questions;
    const newId = `${sectionId}_custom_${Date.now()}`;

    setSections(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        questions: [
          ...prev[sectionId].questions,
          {
            id: newId,
            question: newQuestionText.trim(),
            followUp: newQuestionFollowUp.trim() || null
          }
        ]
      }
    }));

    setNewQuestionText('');
    setNewQuestionFollowUp('');
    setShowAddQuestion(null);
    markChanged();
  };

  const moveQuestion = (sectionId, fromIndex, direction) => {
    const toIndex = fromIndex + direction;
    setSections(prev => {
      const updated = { ...prev };
      const questions = [...updated[sectionId].questions];
      if (toIndex < 0 || toIndex >= questions.length) return prev;
      [questions[fromIndex], questions[toIndex]] = [questions[toIndex], questions[fromIndex]];
      updated[sectionId] = { ...updated[sectionId], questions };
      return updated;
    });
    markChanged();
  };

  // =============================================
  // Section editing
  // =============================================

  const updateSectionField = (sectionId, field, value) => {
    setSections(prev => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], [field]: value }
    }));
    markChanged();
  };

  const addSection = () => {
    if (!newSectionTitle.trim()) return;

    const newId = `custom_${Date.now()}`;
    setSections(prev => ({
      ...prev,
      [newId]: {
        id: newId,
        title: newSectionTitle.trim(),
        description: newSectionDescription.trim() || '',
        icon: newSectionIcon || '📝',
        required: false,
        questions: []
      }
    }));
    setSectionOrder(prev => [...prev, newId]);
    setExpandedSections(prev => ({ ...prev, [newId]: true }));

    setNewSectionTitle('');
    setNewSectionDescription('');
    setNewSectionIcon('📝');
    setShowAddSection(false);
    markChanged();
  };

  const deleteSection = (sectionId) => {
    setSections(prev => {
      const updated = { ...prev };
      delete updated[sectionId];
      return updated;
    });
    setSectionOrder(prev => prev.filter(id => id !== sectionId));
    setShowDeleteConfirm(null);
    markChanged();
  };

  const moveSection = (sectionId, direction) => {
    setSectionOrder(prev => {
      const idx = prev.indexOf(sectionId);
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      // Don't allow moving above consent
      if (prev[newIdx] === 'consent' || sectionId === 'consent') return prev;
      const updated = [...prev];
      [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
      return updated;
    });
    markChanged();
  };

  // =============================================
  // Counts
  // =============================================

  const totalQuestions = Object.values(sections).reduce((sum, s) => sum + (s.questions?.length || 0), 0);
  const nonConsentQuestions = Object.entries(sections)
    .filter(([id]) => id !== 'consent')
    .reduce((sum, [, s]) => sum + (s.questions?.length || 0), 0);

  // =============================================
  // Gate: admin only
  // =============================================

  if (!user?.isSuperAdmin && (!user?.communityRole || user.communityRole !== 'admin')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md text-center">
          <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900 mb-2">Admin Access Required</h2>
          <p className="text-gray-500 mb-4">Only community admins can manage interview questions.</p>
          <button onClick={() => navigate(-1)} className="text-quiltt-purple hover:underline">Go Back</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-quiltt-purple border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading questions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/community/${communityId}`)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Interview Questions</h1>
                <p className="text-sm text-gray-500">
                  {sectionOrder.length} sections · {nonConsentQuestions} questions
                  {isCustomized && <span className="ml-2 text-quiltt-purple">(customized)</span>}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isCustomized && (
                <button
                  onClick={() => setShowResetConfirm(true)}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Reset to defaults"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span className="hidden sm:inline">Reset</span>
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-quiltt-purple hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {/* Messages */}
        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
            <Check className="w-4 h-4 flex-shrink-0" />
            {successMessage}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {hasChanges && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            You have unsaved changes. Click "Save Changes" to apply them.
          </div>
        )}

        {/* Sections */}
        <div className="space-y-4">
          {sectionOrder.map((sectionId, sectionIdx) => {
            const section = sections[sectionId];
            if (!section) return null;
            const isExpanded = expandedSections[sectionId];
            const isConsent = sectionId === 'consent';

            return (
              <div key={sectionId} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Section header */}
                <div
                  className="flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleSection(sectionId)}
                >
                  {/* Reorder arrows */}
                  <div className="flex flex-col gap-0.5" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => moveSection(sectionId, -1)}
                      disabled={sectionIdx === 0 || isConsent || sectionOrder[sectionIdx - 1] === 'consent'}
                      className="p-0.5 text-gray-400 hover:text-gray-600 disabled:text-gray-200 disabled:cursor-not-allowed"
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => moveSection(sectionId, 1)}
                      disabled={sectionIdx === sectionOrder.length - 1 || isConsent}
                      className="p-0.5 text-gray-400 hover:text-gray-600 disabled:text-gray-200 disabled:cursor-not-allowed"
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <span className="text-xl">{section.icon}</span>

                  <div className="flex-1 min-w-0">
                    {editingSection === sectionId ? (
                      <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                        <input
                          type="text"
                          value={section.title}
                          onChange={e => updateSectionField(sectionId, 'title', e.target.value)}
                          className="flex-1 px-2 py-1 text-sm font-medium border border-gray-300 rounded focus:ring-1 focus:ring-quiltt-500 outline-none"
                          autoFocus
                        />
                        <button
                          onClick={() => setEditingSection(null)}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{section.title}</h3>
                        {!isConsent && (
                          <button
                            onClick={e => { e.stopPropagation(); setEditingSection(sectionId); }}
                            className="p-1 text-gray-400 hover:text-gray-600 rounded opacity-0 group-hover:opacity-100"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    )}
                    <p className="text-sm text-gray-500">
                      {section.questions.length} question{section.questions.length !== 1 ? 's' : ''}
                      {section.description && <span className="ml-1">· {section.description}</span>}
                    </p>
                  </div>

                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    {!isConsent && (
                      <button
                        onClick={() => setShowDeleteConfirm({ type: 'section', sectionId })}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Delete section"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </div>

                {/* Questions list */}
                {isExpanded && (
                  <div className="border-t border-gray-100">
                    {section.questions.map((q, qIdx) => (
                      <div
                        key={q.id}
                        className="px-5 py-3 border-b border-gray-50 last:border-b-0 hover:bg-gray-50 transition-colors group"
                      >
                        <div className="flex items-start gap-3">
                          {/* Reorder */}
                          <div className="flex flex-col gap-0.5 pt-1">
                            <button
                              onClick={() => moveQuestion(sectionId, qIdx, -1)}
                              disabled={qIdx === 0}
                              className="p-0.5 text-gray-300 hover:text-gray-500 disabled:text-gray-200 disabled:cursor-not-allowed"
                            >
                              <ChevronUp className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => moveQuestion(sectionId, qIdx, 1)}
                              disabled={qIdx === section.questions.length - 1}
                              className="p-0.5 text-gray-300 hover:text-gray-500 disabled:text-gray-200 disabled:cursor-not-allowed"
                            >
                              <ChevronDown className="w-3 h-3" />
                            </button>
                          </div>

                          {/* Question number */}
                          <span className="text-xs font-mono text-gray-400 pt-1.5 w-6 text-right flex-shrink-0">{qIdx + 1}</span>

                          {/* Question content */}
                          <div className="flex-1 min-w-0">
                            {editingQuestion?.sectionId === sectionId && editingQuestion?.questionIndex === qIdx ? (
                              <div className="space-y-2">
                                <textarea
                                  value={q.question}
                                  onChange={e => updateQuestion(sectionId, qIdx, 'question', e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-quiltt-500 outline-none resize-none"
                                  rows={2}
                                  autoFocus
                                />
                                <textarea
                                  value={q.followUp || ''}
                                  onChange={e => updateQuestion(sectionId, qIdx, 'followUp', e.target.value || null)}
                                  placeholder="Follow-up prompt (optional)"
                                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-quiltt-500 outline-none resize-none text-gray-600"
                                  rows={1}
                                />
                                <button
                                  onClick={() => setEditingQuestion(null)}
                                  className="text-xs text-quiltt-purple hover:underline"
                                >
                                  Done editing
                                </button>
                              </div>
                            ) : (
                              <div
                                className="cursor-pointer"
                                onClick={() => setEditingQuestion({ sectionId, questionIndex: qIdx })}
                              >
                                <p className="text-sm text-gray-900 leading-relaxed">{q.question}</p>
                                {q.followUp && (
                                  <p className="text-xs text-gray-500 mt-1 italic">↳ {q.followUp}</p>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => setEditingQuestion({ sectionId, questionIndex: qIdx })}
                              className="p-1.5 text-gray-400 hover:text-quiltt-purple hover:bg-quiltt-50 rounded transition-colors"
                              title="Edit question"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => setShowDeleteConfirm({ type: 'question', sectionId, questionIndex: qIdx })}
                              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                              title="Delete question"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Add question button */}
                    {showAddQuestion === sectionId ? (
                      <div className="px-5 py-4 bg-gray-50 border-t border-gray-100">
                        <textarea
                          value={newQuestionText}
                          onChange={e => setNewQuestionText(e.target.value)}
                          placeholder="Type the interview question..."
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-quiltt-500 outline-none resize-none mb-2"
                          rows={2}
                          autoFocus
                        />
                        <textarea
                          value={newQuestionFollowUp}
                          onChange={e => setNewQuestionFollowUp(e.target.value)}
                          placeholder="Follow-up prompt (optional)"
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-quiltt-500 outline-none resize-none text-gray-600 mb-3"
                          rows={1}
                        />
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => addQuestion(sectionId)}
                            disabled={!newQuestionText.trim()}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-quiltt-purple hover:bg-purple-700 disabled:bg-gray-300 rounded-lg transition-colors"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            Add Question
                          </button>
                          <button
                            onClick={() => { setShowAddQuestion(null); setNewQuestionText(''); setNewQuestionFollowUp(''); }}
                            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowAddQuestion(sectionId)}
                        className="w-full px-5 py-3 text-sm text-gray-500 hover:text-quiltt-purple hover:bg-gray-50 border-t border-gray-100 flex items-center gap-2 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Add question to {section.title}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Add section button */}
          {showAddSection ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h3 className="font-medium text-gray-900 mb-3">New Section</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={newSectionIcon}
                    onChange={e => setNewSectionIcon(e.target.value)}
                    className="w-12 text-center px-2 py-2 border border-gray-300 rounded-lg text-xl"
                    maxLength={2}
                    placeholder="📝"
                  />
                  <input
                    type="text"
                    value={newSectionTitle}
                    onChange={e => setNewSectionTitle(e.target.value)}
                    placeholder="Section title (e.g., Travel & Adventures)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-quiltt-500 outline-none"
                    autoFocus
                  />
                </div>
                <input
                  type="text"
                  value={newSectionDescription}
                  onChange={e => setNewSectionDescription(e.target.value)}
                  placeholder="Brief description (optional)"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-quiltt-500 outline-none text-sm text-gray-600"
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={addSection}
                    disabled={!newSectionTitle.trim()}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-quiltt-purple hover:bg-purple-700 disabled:bg-gray-300 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Create Section
                  </button>
                  <button
                    onClick={() => { setShowAddSection(false); setNewSectionTitle(''); setNewSectionDescription(''); }}
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddSection(true)}
              className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:text-quiltt-purple hover:border-quiltt-purple flex items-center justify-center gap-2 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add New Section
            </button>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="font-semibold text-gray-900 mb-2">
              Delete {showDeleteConfirm.type === 'section' ? 'Section' : 'Question'}?
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {showDeleteConfirm.type === 'section'
                ? `This will remove "${sections[showDeleteConfirm.sectionId]?.title}" and all its questions. Existing responses won't be deleted but won't be shown in future interviews.`
                : 'This question will be removed from the interview. Existing responses won\'t be deleted.'}
            </p>
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (showDeleteConfirm.type === 'section') {
                    deleteSection(showDeleteConfirm.sectionId);
                  } else {
                    deleteQuestion(showDeleteConfirm.sectionId, showDeleteConfirm.questionIndex);
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset confirmation modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="font-semibold text-gray-900 mb-2">Reset to Default Questions?</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will replace all customized questions with the original Life Story question bank. Any custom sections or questions will be lost. Existing recorded responses won't be affected.
            </p>
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                {saving ? 'Resetting...' : 'Reset to Defaults'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
